import { useEffect, useState } from "react";
import {
  kerningPairsSchema,
  type KerningPair,
  type FontFaceDescriptor,
} from "@proof/core";
import { Button, Control } from "./components";

export function KerningPairs({
  value,
  fonts,
  enabled,
  onChange,
}: {
  value?: KerningPair[];
  fonts: FontFaceDescriptor[];
  enabled: boolean;
  onChange: (value: KerningPair[] | undefined) => void;
}) {
  const encoded = JSON.stringify(value || []);
  const rows = () =>
    (value || []).map((pair) => ({
      ...pair,
      adjustment: String(pair.adjustment),
      fontFaceId: pair.fontFaceId || "",
    }));
  const [draft, setDraft] = useState(rows),
    [error, setError] = useState("");
  useEffect(() => {
    setDraft(rows());
    setError("");
  }, [encoded]);
  const update = (index: number, patch: Partial<(typeof draft)[number]>) =>
    setDraft(
      draft.map((pair, i) => (i === index ? { ...pair, ...patch } : pair)),
    );
  const save = () => {
    const parsed = kerningPairsSchema.safeParse(
      draft.map((pair) => ({
        ...pair,
        adjustment:
          pair.adjustment.trim() === "" ? NaN : Number(pair.adjustment),
        fontFaceId: pair.fontFaceId || undefined,
      })),
    );
    if (!parsed.success) {
      setError(parsed.error.issues.map((issue) => issue.message).join(". "));
      return;
    }
    if (
      parsed.data.some(
        (pair) =>
          pair.fontFaceId && !fonts.some((font) => font.id === pair.fontFaceId),
      )
    ) {
      setError("Choose an available managed font.");
      return;
    }
    onChange(parsed.data.length ? parsed.data : undefined);
    setError("");
  };
  return (
    <details className="kerning-pairs">
      <summary>Custom kerning pairs ({value?.length || 0})</summary>
      <p className="inspector-note">
        Adjust spacing inside words in 1/1000 em. Positive values add space;
        negative values tighten it. Pairs apply between separate glyphs. A
        font-specific pair takes precedence over an all-fonts pair.
      </p>
      {!enabled && (
        <p role="status">Choose a managed font to use custom kerning.</p>
      )}
      <fieldset
        disabled={!enabled}
        style={{ border: 0, padding: 0, minWidth: 0 }}
      >
        {draft.map((pair, index) => (
          <section
            className="panel padded"
            key={index}
            aria-label={`Kerning pair ${index + 1}`}
          >
            <div className="control-grid">
              <Control label={`Pair ${index + 1} left character`}>
                <input
                  value={pair.left}
                  maxLength={2}
                  onChange={(event) =>
                    update(index, { left: event.target.value })
                  }
                />
              </Control>
              <Control label={`Pair ${index + 1} right character`}>
                <input
                  value={pair.right}
                  maxLength={2}
                  onChange={(event) =>
                    update(index, { right: event.target.value })
                  }
                />
              </Control>
            </div>
            <Control label={`Pair ${index + 1} adjustment`}>
              <input
                type="number"
                min={-1000}
                max={1000}
                step={0.1}
                value={pair.adjustment}
                onChange={(event) =>
                  update(index, { adjustment: event.target.value })
                }
              />
            </Control>
            <Control label={`Pair ${index + 1} font`}>
              <select
                value={pair.fontFaceId}
                onChange={(event) =>
                  update(index, { fontFaceId: event.target.value })
                }
              >
                <option value="">All fonts in this layer</option>
                {fonts.map((font) => (
                  <option key={font.id} value={font.id}>
                    {font.name}
                  </option>
                ))}
              </select>
            </Control>
            <Button
              aria-label={`Remove kerning pair ${index + 1}`}
              onClick={() => setDraft(draft.filter((_, i) => i !== index))}
            >
              Remove pair
            </Button>
          </section>
        ))}
        <div className="button-row">
          <Button
            disabled={draft.length >= 128}
            onClick={() =>
              setDraft([
                ...draft,
                { left: "", right: "", adjustment: "0", fontFaceId: "" },
              ])
            }
          >
            Add kerning pair
          </Button>
          <Button onClick={save}>Apply kerning pairs</Button>
          <Button
            onClick={() => {
              setDraft(rows());
              setError("");
            }}
          >
            Reset pair edits
          </Button>
        </div>
      </fieldset>
      {error && <p role="alert">{error}</p>}
    </details>
  );
}
